import type { Db } from "./client";
import { getUserId } from "./client";
import { throwIfPgError, NotFoundError } from "./errors";
import type { PhotoRow, PhotoInsert } from "@garden/types/db";

/** Insert a row in `photo` for a freshly uploaded R2 object. The Worker
 *  has already streamed the file into R2 and returned the key; this is
 *  just the metadata bookkeeping. RLS now enforces membership of
 *  `garden_id`. The caller MUST supply `garden_id` — photo has no FK
 *  ancestor for the trigger (0011) to derive it from. */
export async function createPhoto(
  client: Db,
  input: {
    garden_id: string;
    r2_key: string;
    width_px?: number | null;
    height_px?: number | null;
    taken_date?: string | null;
    caption?: string | null;
  },
): Promise<PhotoRow> {
  const userId = await getUserId(client);
  const row: PhotoInsert = {
    user_id: userId,
    garden_id: input.garden_id,
    r2_key: input.r2_key,
    width_px: input.width_px ?? null,
    height_px: input.height_px ?? null,
    taken_date: input.taken_date ?? null,
    caption: input.caption ?? null,
    tag_status: "untagged",
  };
  const { data, error } = await client
    .from("photo")
    .insert(row as PhotoInsert) // `as` cast sanctioned at Supabase boundary per spec §5
    .select("*")
    .single();
  throwIfPgError(error);
  return data as PhotoRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function countUntaggedPhotos(
  client: Db,
  gardenId?: string,
): Promise<number> {
  let q = client
    .from("photo")
    .select("id", { head: true, count: "exact" })
    .eq("tag_status", "untagged");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { count, error } = await q;
  throwIfPgError(error);
  return count ?? 0;
}

export async function listUntaggedPhotos(
  client: Db,
  gardenId?: string,
): Promise<PhotoRow[]> {
  let q = client
    .from("photo")
    .select("*")
    .eq("tag_status", "untagged");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q.order("uploaded_at", { ascending: false });
  throwIfPgError(error);
  return (data ?? []) as PhotoRow[];
}

export async function getPhoto(client: Db, id: string): Promise<PhotoRow> {
  const { data, error } = await client
    .from("photo")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("photo", id);
  return data as PhotoRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function markPhotoTagged(client: Db, id: string): Promise<void> {
  const { error } = await client
    .from("photo")
    .update({ tag_status: "tagged" })
    .eq("id", id);
  throwIfPgError(error);
}
